import React from 'react';
import clsx from 'clsx';
import i18next from 'i18next';
import { withTranslation } from 'react-i18next';
import Input from '../../../components/input';
import Button from '../../../components/button';
import CheckBox from '../../../components/checkbox';
import Select from '../../../components/select';
import Image from '../../../components/image';
import Source from '../../../components/source';
import Label from '../../../components/label';
import styles from './view.scss';
import FacebookButton from '../../../components/facebook';
import GoogleButton from '../../../components/google';
import OutsideButton from '../../../components/outside';

const nameTypes = {
  first: 'first_name',
  last: 'last_name',
  full: 'full_name',
};

function View(props) {
  const {
    t,
    i18n,
    tokenValue,
    errorToken,
    allowEmail,
    allowPhone,
    nameValue,
    errorName,
    captureName,
    nameType,
    countryCode,
    showCountryCode,
    presenter,
    disableCountryPicker,
    loading,
    primaryConsent,
    errorPrimaryConsent,
    marketingConsent,
    showMarketingConsent,
    showPrimaryConsent,
    captureAdditional,
    selectedAdditional,
    errorAdditional,
    showFacebook,
    showGoogle,
    showOutsideOauth,
    oauthOnly,
    customInput,
    advertiserName,
    marketingConsentTerms,
    languagePolicies,
  } = props;

  let label = null;
  let placeholder = null;
  let type = 'text';
  if (allowEmail && allowPhone) {
    label =
      i18next.exists('claim.landing.input_label_email_phone') &&
      t('claim.landing.input_label_email_phone');
    placeholder =
      i18next.exists('claim.landing.input_placeholder_email_phone') &&
      t('claim.landing.input_placeholder_email_phone');
  } else if (allowEmail) {
    label =
      i18next.exists('claim.landing.input_label_email') && t('claim.landing.input_label_email');
    placeholder =
      i18next.exists('claim.landing.input_placeholder_email') &&
      t('claim.landing.input_placeholder_email');
    type = 'email';
  } else if (allowPhone) {
    label =
      i18next.exists('claim.landing.input_label_phone') && t('claim.landing.input_label_phone');
    placeholder =
      i18next.exists('claim.landing.input_placeholder_phone') &&
      t('claim.landing.input_placeholder_phone');
    type = 'tel';
  }

  let nameLabel = 'claim.landing.input_label_full_name';
  let namePlaceholder = 'claim.landing.input_placeholder_full_name';

  if (nameType === nameTypes.first) {
    nameLabel = 'claim.landing.input_label_first_name';
    namePlaceholder = 'claim.landing.input_placeholder_first_name';
  } else if (nameType === nameTypes.last) {
    nameLabel = 'claim.landing.input_label_last_name';
    namePlaceholder = 'claim.landing.input_placeholder_last_name';
  }

  const showClientConsent =
    i18next.exists('claim.landing.consent_client') && t('claim.landing.consent_client');

  let consentPrimaryText = '';

  if (languagePolicies) {
    // Primary could be: Platform Only, Platform + Client, or Custom Only
    // For Custom, locale key to overridden
    if (showPrimaryConsent) {
      // Add platform or custom
      consentPrimaryText = t('claim.landing.consent_primary_tokenized', {
        terms: languagePolicies.platform_terms,
        privacy: languagePolicies.platform_privacy,
        nft: languagePolicies.platform_nft,
      });
      // Add client text if needed (only for platform)
      if (showClientConsent) {
        consentPrimaryText += ` ${t('claim.landing.consent_client')}`;
      }
    }
  }

  return (
    <div className={clsx('claim-landing-container', styles.container)}>
      <video
        className={clsx('claim-landing-background-video-container', styles.videoContainer)}
        autoPlay
        muted
        loop
      >
        <Source type="video/mp4" className={clsx('claim-landing-background-video', styles.video)} />
      </video>
      <div className={clsx('claim-landing-background-image-container', styles.imageContainer)}>
        <Image
          alt=""
          src={
            i18n.exists('claim.landing.background_image.src')
              ? t('claim.landing.background_image.src')
              : ''
          }
          className={clsx('claim-landing-background-image', styles.image)}
        />
      </div>
      <div className={clsx('claim-landing-inner-container', styles.innerContainer)}>
        <Label className={clsx('claim-landing-text', styles.text)} text={t('claim.landing.text')} />

        {captureName && (
          <Input
            className={clsx(
              styles.input,
              'claim-landing-input',
              'claim-landing-input-name',
              errorName && 'error'
            )}
            value={nameValue}
            label={i18next.exists(nameLabel) && t(nameLabel)}
            placeholder={i18next.exists(namePlaceholder) && t(namePlaceholder)}
            helperText={errorName && t(errorName)}
            onChange={(value) => presenter.onNameChange(value)}
            type="text"
            disabled={loading}
          />
        )}

        {!oauthOnly && (
          <Input
            className={clsx(
              styles.input,
              'claim-landing-input',
              'claim-landing-input-token',
              errorToken && 'error'
            )}
            value={tokenValue}
            label={label}
            placeholder={placeholder}
            leftText={showCountryCode && countryCode}
            helperText={errorToken && t(errorToken)}
            onChange={(value) => presenter.onTokenChange(value)}
            onLeftClick={() => presenter.onCountryCodeClick()}
            disableLeft={disableCountryPicker}
            type={type}
            disabled={loading}
          />
        )}

        {captureAdditional && (
          <Select
            className={clsx(
              styles.select,
              'claim-landing-select-additional',
              errorAdditional && 'error'
            )}
            label={
              i18next.exists('claim.landing.additional.label') &&
              t('claim.landing.additional.label')
            }
            placeholder={
              i18next.exists('claim.landing.additional.placeholder') &&
              t('claim.landing.additional.placeholder')
            }
            items={captureAdditional.map((item) => ({ value: item.value, name: t(item.name) }))}
            onSelect={(value) => presenter.onAdditionalSelect(value)}
            helperText={errorAdditional && t(errorAdditional)}
            selected={selectedAdditional}
            disabled={loading}
          />
        )}
        {customInput?.map((custom, index) => {
          return (
            <Input
              // eslint-disable-next-line react/no-array-index-key
              key={`custom-input-${index}`}
              className={clsx(
                styles.input,
                'claim-landing-input',
                `claim-landing-input-custom${index}`,
                custom.error && 'error'
              )}
              value={custom.value || ''}
              label={t(custom.name)}
              placeholder={custom.placeholder && t(custom.placeholder)}
              helperText={custom.error && t(custom.error)}
              onChange={(value) => presenter.onCustomInputChange(value, custom)}
              type="text"
              disabled={loading}
            />
          );
        })}
        {showPrimaryConsent && (
          <CheckBox
            className={clsx(
              styles.checkbox,
              'claim-landing-checkbox',
              'claim-landing-checkbox-primary',
              errorPrimaryConsent && 'error'
            )}
            text={consentPrimaryText}
            helperText={errorPrimaryConsent && t(errorPrimaryConsent)}
            disabled={loading}
            checked={primaryConsent}
            onCheckedToggle={(consent) => presenter.onPrimaryConsentToggle(consent)}
          />
        )}
        {showMarketingConsent && (
          <CheckBox
            className={clsx(
              styles.checkbox,
              'claim-landing-checkbox',
              'claim-landing-checkbox-marketing'
            )}
            text={t('claim.landing.consent_marketing', {
              advertiser: advertiserName,
              terms: marketingConsentTerms,
              interpolation: { escapeValue: false },
            })}
            disabled={loading}
            checked={marketingConsent}
            onCheckedToggle={(consent) => presenter.onMarketingConsentToggle(consent)}
          />
        )}
        {!oauthOnly && (
          <Button
            className={clsx('claim-landing-button', styles.button)}
            text={loading ? t('claim.landing.loading_text') : t('claim.landing.button_text')}
            disabled={loading}
            onClick={() => presenter.onNextClick()}
          />
        )}
        {showFacebook && (
          <FacebookButton
            className={clsx('claim-landing-facebook', styles.facebook)}
            text={t('claim.landing.facebook')}
            disabled={loading}
            onClick={() => presenter.onFacebookClick()}
          />
        )}
        {showGoogle && (
          <GoogleButton
            className={clsx('claim-landing-google', styles.google)}
            text={t('claim.landing.google')}
            disabled={loading}
            onClick={() => presenter.onGoogleClick()}
          />
        )}
        {showOutsideOauth && (
          <OutsideButton
            className={clsx('claim-landing-outside', styles.outsideOauth)}
            text={t('claim.landing.outside_oauth')}
            disabled={loading}
            onClick={() => presenter.onOutsideClick()}
          />
        )}
        <div className={clsx('claim-landing-footer-space', styles.footerSpace)} />
        <Label
          className={clsx('claim-landing-footer', styles.footer)}
          text={t('claim.landing.footer')}
        />
      </div>
    </div>
  );
}

export default withTranslation()(View);
